// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { User, Camera, Mail, Phone, MapPin, Globe, Briefcase, Save, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/providers/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { getPrimaryShippingAddress, syncProfileToShippingAddress } from '@/utils/userInfo';
import CollectionsManager from './CollectionsManager';
import AwardsShowcasePicker from './AwardsShowcasePicker';

interface ProfileData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  bio: string;
  title: string;
  website: string;
  preferred_contact_method: string;
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

const MyProfileTab: React.FC = () => {
  const { user } = useAuth();
  const [profileData, setProfileData] = useState<ProfileData>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    bio: '',
    title: '',
    website: '',
    preferred_contact_method: 'email',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    loadProfile();
  }, [user]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      
      if (!user) return;

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      const shipping = await getPrimaryShippingAddress(user.id);

      if (data || shipping) {
        setProfileData({
          first_name: data?.first_name || '',
          last_name: data?.last_name || '',
          email: data?.email || user.email || '',
          phone: data?.phone || shipping?.phone || '',
          bio: data?.bio || '',
          title: data?.title || '',
          website: data?.website || '',
          preferred_contact_method: data?.preferred_contact_method || 'email',
          address_line_1: shipping?.address_line_1 || data?.address_line_1 || '',
          address_line_2: shipping?.address_line_2 || data?.address_line_2 || '',
          city: shipping?.city || data?.city || '',
          state: shipping?.state_province || data?.state || '',
          postal_code: shipping?.postal_code || data?.postal_code || '',
          country: shipping?.country || data?.country || 'US'
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to load profile data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (!user) return;

      // Update auth metadata if email changed
      if (profileData.email !== user.email) {
        const { error: authError } = await supabase.auth.updateUser({
          email: profileData.email
        });
        if (authError) throw authError;
      }

      // Update profile data
      const { error } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          ...profileData,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      if (profileData.address_line_1 && profileData.city && profileData.state && profileData.postal_code) {
        const syncResult = await syncProfileToShippingAddress(user.id, {
          first_name: profileData.first_name,
          last_name: profileData.last_name,
          phone: profileData.phone,
          address_line_1: profileData.address_line_1,
          address_line_2: profileData.address_line_2,
          city: profileData.city,
          state: profileData.state,
          postal_code: profileData.postal_code,
          country: profileData.country
        });

        if (!syncResult.success) {
          toast({
            title: 'Shipping Address',
            description: syncResult.message,
            variant: 'destructive'
          });
        }
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      
      toast({
        title: 'Success',
        description: 'Profile & primary shipping address updated'
      });
    } catch (error) {
      console.error('Error saving profile:', error);
      toast({
        title: 'Error',
        description: 'Failed to save profile',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (first?: string, last?: string, email?: string) => {
    if (first && last) {
      return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
    }
    if (email) {
      return email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex items-center gap-3 text-white/70">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span className="font-light">Loading profile...</span>
        </div>
      </div>
    );
  }

  return (
    <ScrollArea className="h-full">
      <div className="p-8 space-y-8">
        {/* Profile Header Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Card className="bg-white/5 backdrop-blur-xl border-white/20 shadow-xl">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-start gap-8">
                <div className="flex flex-col items-center gap-4">
                  <Avatar className="h-24 w-24 border-4 border-white/20 shadow-lg">
                    <AvatarImage src="" alt="Profile" />
                    <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-teal-500 text-white text-2xl font-medium">
                      {getInitials(profileData.first_name, profileData.last_name, profileData.email)}
                    </AvatarFallback>
                  </Avatar>
                  <Button variant="outline" size="sm" className="bg-background text-primary border-primary/30 hover:bg-primary/10">
                    <Camera className="h-4 w-4 mr-2" />
                    Change Photo
                  </Button>
                </div>
                
                <div className="flex-1 space-y-6">
                  <div>
                    <h2 className="text-2xl font-light text-white mb-2">
                      {profileData.first_name || profileData.last_name 
                        ? `${profileData.first_name} ${profileData.last_name}`.trim()
                        : 'Your Profile'
                      }
                    </h2>
                    {profileData.title && (
                      <p className="text-white/70 font-light">{profileData.title}</p>
                    )}
                    <p className="text-white/60 text-sm font-light mt-1">{profileData.email}</p>
                  </div>
                  
                  {profileData.bio && (
                    <p className="text-white/80 font-light leading-relaxed">{profileData.bio}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Profile Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
        >
          {/* Basic Information */}
          <Card className="bg-white/5 backdrop-blur-xl border-white/20 shadow-xl">
            <CardHeader>
              <CardTitle className="text-white font-light flex items-center gap-3">
                <User className="h-5 w-5 text-emerald-400" />
                Basic Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-white/80 font-light">First Name</Label>
                  <Input
                    id="first_name"
                    value={profileData.first_name}
                    onChange={(e) => setProfileData(prev => ({ ...prev, first_name: e.target.value }))}
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-400/50"
                    placeholder="Enter first name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-white/80 font-light">Last Name</Label>
                  <Input
                    id="last_name"
                    value={profileData.last_name}
                    onChange={(e) => setProfileData(prev => ({ ...prev, last_name: e.target.value }))}
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-400/50"
                    placeholder="Enter last name"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="title" className="text-white/80 font-light">Title</Label>
                <Input
                  id="title"
                  value={profileData.title}
                  onChange={(e) => setProfileData(prev => ({ ...prev, title: e.target.value }))}
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-400/50"
                  placeholder="e.g., Art Collector, Gallery Owner"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="bio" className="text-white/80 font-light">Bio</Label>
                <Textarea
                  id="bio"
                  value={profileData.bio}
                  onChange={(e) => setProfileData(prev => ({ ...prev, bio: e.target.value }))}
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-400/50 resize-none"
                  placeholder="Tell us about yourself..."
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card className="bg-white/5 backdrop-blur-xl border-white/20 shadow-xl">
            <CardHeader>
              <CardTitle className="text-white font-light flex items-center gap-3">
                <Mail className="h-5 w-5 text-teal-400" />
                Contact Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white/80 font-light">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-400/50"
                  placeholder="Enter email address"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone" className="text-white/80 font-light">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={profileData.phone}
                  onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-400/50"
                  placeholder="Enter phone number"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="website" className="text-white/80 font-light">Website</Label>
                <Input
                  id="website"
                  type="url"
                  value={profileData.website}
                  onChange={(e) => setProfileData(prev => ({ ...prev, website: e.target.value }))}
                  className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-400/50"
                  placeholder="https://yourwebsite.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="preferred_contact" className="text-white/80 font-light">Preferred Contact Method</Label>
                <Select 
                  value={profileData.preferred_contact_method} 
                  onValueChange={(value) => setProfileData(prev => ({ ...prev, preferred_contact_method: value }))}
                >
                  <SelectTrigger className="bg-white/5 border-white/20 text-white">
                    <SelectValue placeholder="Select contact method" />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/20">
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Address Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <Card className="bg-white/5 backdrop-blur-xl border-white/20 shadow-xl">
            <CardHeader>
              <CardTitle className="text-white font-light flex items-center gap-3">
                <MapPin className="h-5 w-5 text-blue-400" />
                Primary Address & Shipping
              </CardTitle>
              <p className="text-white/60 text-sm mt-2">
                Updates here automatically sync to your primary shipping address. Manage additional addresses in the Shipping Addresses tab.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="address_line_1" className="text-white/80 font-light">Address Line 1</Label>
                  <Input
                    id="address_line_1"
                    value={profileData.address_line_1}
                    onChange={(e) => setProfileData(prev => ({ ...prev, address_line_1: e.target.value }))}
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-400/50"
                    placeholder="Street address"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="address_line_2" className="text-white/80 font-light">Address Line 2</Label>
                  <Input
                    id="address_line_2"
                    value={profileData.address_line_2}
                    onChange={(e) => setProfileData(prev => ({ ...prev, address_line_2: e.target.value }))}
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-400/50"
                    placeholder="Apt, suite, etc. (optional)"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="city" className="text-white/80 font-light">City</Label>
                  <Input
                    id="city"
                    value={profileData.city}
                    onChange={(e) => setProfileData(prev => ({ ...prev, city: e.target.value }))}
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-400/50"
                    placeholder="Enter city"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="state" className="text-white/80 font-light">State/Province</Label>
                  <Input
                    id="state"
                    value={profileData.state}
                    onChange={(e) => setProfileData(prev => ({ ...prev, state: e.target.value }))}
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-400/50"
                    placeholder="Enter state/province"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="postal_code" className="text-white/80 font-light">Postal Code</Label>
                  <Input
                    id="postal_code"
                    value={profileData.postal_code}
                    onChange={(e) => setProfileData(prev => ({ ...prev, postal_code: e.target.value }))}
                    className="bg-white/5 border-white/20 text-white placeholder:text-white/40 focus:border-emerald-400/50"
                    placeholder="Enter postal code"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="country" className="text-white/80 font-light">Country</Label>
                  <Select 
                    value={profileData.country} 
                    onValueChange={(value) => setProfileData(prev => ({ ...prev, country: value }))}
                  >
                    <SelectTrigger className="bg-white/5 border-white/20 text-white">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-white/20">
                      <SelectItem value="US">United States</SelectItem>
                      <SelectItem value="CA">Canada</SelectItem>
                      <SelectItem value="GB">United Kingdom</SelectItem>
                      <SelectItem value="AU">Australia</SelectItem>
                      <SelectItem value="DE">Germany</SelectItem>
                      <SelectItem value="FR">France</SelectItem>
                      <SelectItem value="JP">Japan</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Collections and Awards stacked (1x1) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.25 }}
          className="space-y-8"
        >
          <CollectionsManager />
          <AwardsShowcasePicker />
        </motion.div>

        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="flex justify-end"
        >
          <Button
            onClick={handleSave}
            disabled={saving}
            className={cn(
              "px-8 py-3 rounded-xl font-medium transition-all duration-300",
              saved 
                ? "bg-green-500/20 border-green-400/50 text-green-400 hover:bg-green-500/30" 
                : "bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white shadow-lg hover:shadow-xl transform hover:scale-105"
            )}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : saved ? (
              <>
                <Check className="h-4 w-4 mr-2" />
                Saved
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </motion.div>
      </div>
    </ScrollArea>
  );
};

export default MyProfileTab;